import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  Lock, FileText, Type, Sun, Quote, Volume2, Square, Copy, Eye, ArrowLeft, Flame,
} from "lucide-react";
import { api, apiError, supporterHeaders, setSupporterCode, BACKEND } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function PaperReader() {
  const { slug } = useParams();
  const { refresh, setUnlocked } = useSite();
  const [paper, setPaper] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [code, setCode] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [sans, setSans] = useState(false);
  const [parchment, setParchment] = useState(false);
  const [citeOpen, setCiteOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24 });

  const load = () => {
    api.get(`/papers/${slug}`, supporterHeaders())
      .then((r) => {
        setPaper(r.data);
        if (!r.data.locked) api.post(`/papers/${slug}/view`).catch(() => {});
      })
      .catch(() => setNotFound(true));
  };

  useEffect(() => {
    setPaper(null);
    setNotFound(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const toc = useMemo(() => {
    if (!paper?.content_md) return [];
    return paper.content_md
      .split("\n")
      .filter((l) => l.startsWith("## "))
      .map((l) => ({ id: l.slice(3).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-"), label: l.slice(3).trim() }));
  }, [paper]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      await api.post("/papers/unlock", { code });
      setSupporterCode(code.trim());
      setUnlocked(true);
      toast.success("Supporter access unlocked. Enjoy the deep dive.");
      load();
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Invalid code"));
    } finally {
      setUnlocking(false);
    }
  };

  const toggleSpeech = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(`${paper.title}. ${paper.abstract}`);
    u.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (notFound) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">404 — Paper not found</p>
        <Link to="/library" className="text-sky-400 text-sm flex items-center gap-2" data-testid="notfound-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to library
        </Link>
      </main>
    );
  }

  if (!paper) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Retrieving paper…</p>
      </main>
    );
  }

  const date = new Date(paper.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const citeApa = `SoverTech Research Fund. (${new Date(paper.created_at).getFullYear()}). ${paper.title} [White paper]. SoverTech Open Archive.`;
  const citeBib = `@misc{sovertech${new Date(paper.created_at).getFullYear()}${paper.slug.replace(/-/g, "")},\n  title = {${paper.title}},\n  author = {{SoverTech Research Fund}},\n  year = {${new Date(paper.created_at).getFullYear()}},\n  howpublished = {SoverTech Open Archive}\n}`;

  /* ---------- PAYWALL ---------- */
  if (paper.locked) {
    return (
      <main className="min-h-screen relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-4">{paper.category} — Supporters Only</p>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8">
            {paper.title}
          </h1>
          <div className="relative">
            <p className="font-serif text-lg leading-[1.85] text-slate-400 select-none" style={{ filter: "blur(1px)" }}>
              {paper.abstract} {paper.abstract}
            </p>
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(15,19,28,0.2) 0%, rgba(7,9,14,0.97) 80%)" }} />
          </div>

          <div data-testid="paywall-modal" className="relative -mt-10 rounded-2xl border border-amber-400/30 bg-[#131824] p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/40 flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-400" />
              </span>
              <div>
                <h2 className="font-display font-bold text-lg">Supporters-only deep dive</h2>
                <p className="text-xs text-slate-500">The full paper, appendices, and datasets unlock with any donation.</p>
              </div>
            </div>
            <form onSubmit={unlock} className="flex flex-col sm:flex-row gap-3">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter supporter access code"
                data-testid="paywall-modal-input"
                className="flex-1 bg-[#07090E] border border-[#1E2638] rounded-md px-4 py-3 text-sm font-mono tracking-widest text-amber-300 placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors"
              />
              <button
                type="submit"
                disabled={unlocking || !code.trim()}
                data-testid="paywall-modal-submit"
                className="bg-amber-400 text-[#07090E] font-semibold text-sm rounded-md px-6 py-3 hover:bg-amber-300 transition-colors disabled:opacity-50"
              >
                Unlock Paper
              </button>
            </form>
            <div className="mt-5 pt-5 border-t border-[#1E2638] flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-slate-500">No code yet? Any donation reveals it instantly.</p>
              <Link
                to="/donate"
                data-testid="paywall-modal-donate-link"
                className="flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
              >
                <Flame className="w-4 h-4" /> Donate &amp; get the code
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* ---------- READER ---------- */
  return (
    <main className={`min-h-screen transition-colors duration-500 ${parchment ? "reader-parchment" : ""}`}>
      <motion.div
        data-testid="reading-progress-bar"
        className="fixed top-0 left-0 right-0 h-[3px] bg-sky-400 origin-left z-[60]"
        style={{ scaleX: progress }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/library" data-testid="reader-back-link" className="reader-chrome inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-sky-400 transition-colors mb-10">
          <ArrowLeft className="w-3.5 h-3.5" /> Library
        </Link>

        <header className="max-w-3xl mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-4">{paper.category}</p>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.12]">
            {paper.title}
          </h1>
          <div className="reader-chrome mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
            <span>{date}</span>
            <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {paper.views || 0} reads</span>
            {paper.tags?.map((t) => (
              <span key={t} className="text-sky-400/70">#{t}</span>
            ))}
          </div>
        </header>

        <div className="reader-chrome flex flex-wrap items-center gap-2.5 mb-12 pb-8 border-b border-[#1E2638]">
          <button onClick={() => setSans(!sans)} data-testid="font-toggle-button" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-[#1E2638] rounded-full px-4 py-2 text-slate-400 hover:border-sky-400/50 hover:text-sky-300 transition-colors">
            <Type className="w-3.5 h-3.5" /> {sans ? "Serif" : "Sans"}
          </button>
          <button onClick={() => setParchment(!parchment)} data-testid="reader-theme-toggle" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-[#1E2638] rounded-full px-4 py-2 text-slate-400 hover:border-amber-400/50 hover:text-amber-300 transition-colors">
            <Sun className="w-3.5 h-3.5" /> {parchment ? "Dark" : "Parchment"}
          </button>
          <button onClick={() => setCiteOpen(true)} data-testid="cite-modal-trigger" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-[#1E2638] rounded-full px-4 py-2 text-slate-400 hover:border-sky-400/50 hover:text-sky-300 transition-colors">
            <Quote className="w-3.5 h-3.5" /> Cite
          </button>
          <button onClick={toggleSpeech} data-testid="audio-player-play" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-[#1E2638] rounded-full px-4 py-2 text-slate-400 hover:border-sky-400/50 hover:text-sky-300 transition-colors">
            {speaking ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            {speaking ? "Stop" : "Listen to abstract"}
          </button>
          {paper.pdf_url && (
            <a
              href={paper.pdf_url.startsWith("http") ? paper.pdf_url : `${BACKEND}${paper.pdf_url}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-pdf-button"
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] bg-sky-400 text-[#07090E] font-semibold rounded-full px-4 py-2 hover:bg-sky-300 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Download PDF
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {toc.length > 1 && (
            <aside className="hidden lg:block lg:col-span-3 sticky top-24" data-testid="toc-nav">
              <p className="reader-chrome font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-5">Contents</p>
              <nav className="flex flex-col gap-3">
                {toc.map((h) => (
                  <a
                    key={h.id}
                    href={`#${h.id}`}
                    data-testid={`toc-link-${h.id}`}
                    className="reader-chrome text-sm text-slate-500 hover:text-sky-400 transition-colors border-l border-[#1E2638] hover:border-sky-400 pl-4"
                  >
                    {h.label}
                  </a>
                ))}
              </nav>
            </aside>
          )}

          <article className={`${toc.length > 1 ? "lg:col-span-7" : "lg:col-span-8 lg:col-start-3"}`}>
            <div className={`paper-prose ${sans ? "font-sans-mode" : ""}`} data-testid="paper-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children }) => {
                    const text = String(children);
                    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    return <h2 id={id}>{children}</h2>;
                  },
                }}
              >
                {paper.content_md}
              </ReactMarkdown>
            </div>

            <div className="mt-16 rounded-2xl border border-amber-400/30 bg-[#131824] p-8 not-prose" data-testid="paper-support-card">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-3">Funded by readers</p>
              <h3 className="font-display font-bold text-xl text-slate-100 mb-3">
                Research like this exists because someone paid it forward.
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                Donations keep every paper open access and finance the next study in the
                pipeline. Join the fund and help the technology grow.
              </p>
              <Link
                to="/donate"
                data-testid="paper-support-donate-btn"
                className="inline-flex items-center gap-2 bg-amber-400 text-[#07090E] font-semibold text-sm rounded-full px-6 py-3 hover:bg-amber-300 transition-colors"
              >
                <Flame className="w-4 h-4" /> Support the next paper
              </Link>
            </div>
          </article>
        </div>
      </div>

      {citeOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[#07090E]/85 backdrop-blur-sm" onClick={() => setCiteOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            data-testid="cite-modal"
            className="relative w-full max-w-lg rounded-2xl border border-[#1E2638] bg-[#131824] p-8"
          >
            <h3 className="font-display font-bold text-xl mb-6">Cite this paper</h3>
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400">APA</p>
                  <button onClick={() => copyText(citeApa, "APA citation")} data-testid="cite-copy-apa" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-300 transition-colors">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <p className="text-sm text-slate-300 bg-[#07090E] border border-[#1E2638] rounded-md p-4 leading-relaxed">{citeApa}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400">BibTeX</p>
                  <button onClick={() => copyText(citeBib, "BibTeX citation")} data-testid="cite-copy-bibtex" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-300 transition-colors">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <pre className="text-xs text-slate-300 bg-[#07090E] border border-[#1E2638] rounded-md p-4 overflow-x-auto font-mono">{citeBib}</pre>
              </div>
            </div>
            <button onClick={() => setCiteOpen(false)} data-testid="cite-modal-close" className="mt-6 w-full border border-[#1E2638] rounded-md py-2.5 text-sm text-slate-300 hover:border-sky-400/50 transition-colors">
              Close
            </button>
          </motion.div>
        </div>
      )}
    </main>
  );
}
